import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type RadioGroupSize = 'sm' | 'md';

type LabelProps = {
  $color?: string;
};

type CircleProps = {
  $size: RadioGroupSize;
};

type OptionTextProps = {
  $color?: string;
};

const sizeStyles: Record<RadioGroupSize, ReturnType<typeof css>> = {
  sm: css`
    width: 16px;
    height: 16px;
  `,
  md: css`
    width: 18px;
    height: 18px;
  `,
};

export const Container = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
`;

export const Legend = styled.legend<LabelProps>`
  display: block;
  margin: 0 0 12px;
  padding: 0;
  font-family: 'Mangueira', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $color }) => $color || theme.colors.primaryText};
`;

export const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
`;

export const OptionLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

export const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

export const Circle = styled('span', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<CircleProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${theme.colors.black};
  border-radius: 50%;
  background: #fff;
  transition: border-color 0.2s ease;

  ${({ $size }) => sizeStyles[$size]}

  &::after {
    content: '';
    width: calc(100% - 8px);
    height: calc(100% - 8px);
    border-radius: 50%;
    background: ${theme.colors.black};
    opacity: 0;
    transform: scale(0.5);
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  input:checked + & {
    border-color: ${theme.colors.black};
  }

  input:checked + &::after {
    opacity: 1;
    transform: scale(1);
  }

  input:focus-visible + & {
    outline: 2px solid ${theme.colors.black};
    outline-offset: 2px;
  }

  input:disabled + & {
    opacity: 0.6;
    cursor: not-allowed;
  }

  input:checked:focus-visible + & {
    outline: none;
  }
`;

export const OptionText = styled.span<OptionTextProps>`
  font-family: 'Mangueira', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $color }) => $color || theme.colors.primaryText};
`;

export const InputWrap = styled.span`
  position: relative;
  display: inline-flex;
`;
